<template>
  <div id="app">
    <div v-if="isMaintenance" class="maintenance-screen">
      <div class="maintenance-card">
        <div class="maintenance-badge">⚠️ Manutenção Temporária</div>

        <div class="maintenance-title">
          <span class="maintenance-icon">🔧</span>
          <h1>Estamos em Manutenção</h1>
        </div>
        <p class="maintenance-subtitle">Voltamos em breve :)</p>

        <p class="maintenance-message">
          Desculpe-nos! Estamos realizando <strong>atualizações e melhorias importantes</strong>
          em nossos sistemas para oferecer uma experiência melhor.
        </p>

        <div class="maintenance-info-box">
          <h3>⏱️ Tempo Estimado</h3>
          <p>Esperamos estar online novamente em aproximadamente <strong>30 a 60 minutos</strong>.</p>
        </div>

        <div class="maintenance-contact">
          <h3>📞 Precisa de Ajuda?</h3>
          <a href="tel:+558134727201" class="maintenance-contact-item">
            <span>☎️</span>
            <span>(81) 3472-7201</span>
          </a>
        </div>

        <div class="maintenance-logo-section">
          <img src="@/assets/uni-logo-horizontal-bordo.svg" alt="Uni Hospitalar" class="maintenance-logo">
          <p class="maintenance-support-info">Obrigado pela paciência enquanto melhoramos nossos serviços.</p>
        </div>
      </div>
    </div>
    <template v-else>
      <div class="page-transition-overlay" :class="{ active: isTransitioning }">
        <div class="transition-logo-container" v-if="isTransitioning">
          <img src="@/assets/uni-logo-horizontal-branco.svg" alt="Uni Hospitalar" class="transition-logo">
        </div>
      </div>
      <transition :name="transitionName" mode="out-in" @before-leave="startTransition" @enter="endTransition">
        <router-view :key="$route.fullPath"></router-view>
      </transition>

      <!-- Cookie Banner Global -->
      <CookieBanner />
    </template>
  </div>
</template>

<script>
import CookieBanner from '@/components/CookieBanner.vue';
import API_CONFIG from '@/config/api';

export default {
  name: 'App',
  components: {
    CookieBanner
  },
  data() {
    return {
      transitionName: 'page',
      isTransitioning: false,
      preloadedImages: [],
      isMaintenance: false,
      maintenancePollInterval: null
    }
  },
  mounted() {
    // Pré-carregamento de todas as imagens de banner
    this.preloadImages();
    this.checkMaintenance();
    // Continua checando enquanto em manutenção, pra voltar sozinho quando
    // o backend desativar (mesma ideia do back/public/maintenance.html)
    this.maintenancePollInterval = setInterval(this.checkMaintenance, 30000);
  },
  beforeUnmount() {
    clearInterval(this.maintenancePollInterval);
  },
  watch: {
    '$route'(to, from) {
      // Determinar a direção da transição
      const toDepth = to.path.split('/').length
      const fromDepth = from.path.split('/').length

      // Usando a transição definida na rota
      if (to.meta && to.meta.transition) {
        this.transitionName = to.meta.transition;
      } else if (fromDepth < toDepth) {
        // Indo para uma rota mais profunda
        this.transitionName = 'slide-left'
      } else if (fromDepth > toDepth) {
        // Voltando para uma rota menos profunda
        this.transitionName = 'slide-right'
      } else {
        // Mesmo nível, usando transição padrão
        this.transitionName = 'page'
      }
    }
  },
  methods: {
    async checkMaintenance() {
      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/api/maintenance-status`);
        const data = await res.json();
        this.isMaintenance = !!data.maintenance;
      } catch (e) {
        // Se o backend estiver inacessível, não força tela de manutenção
        // por conta disso — evita falso positivo em instabilidade de rede.
      }
    },
    startTransition() {
      this.isTransitioning = true;
    },
    endTransition() {
      // Desativa o overlay imediatamente quando a nova página começa a entrar
      this.isTransitioning = false;
    },
    preloadImages() {
      // Lista de todas as imagens de banner usadas nos componentes
      const bannerImages = [
        require('@/assets/uni-logo-horizontal-bordo.svg'),
        require('@/assets/uni-logo-horizontal-bordo-v2.svg'),
        require('@/assets/uni-logo-horizontal-branco.svg'),
        require('@/assets/uni-logo-vertical-branco-principal.svg'),
        require('@/assets/uni-logo-vertical-bordo.svg'),
        require('@/assets/header-conduta-etica.png'),
        require('@/assets/header-contato.jfif'),
        require('@/assets/header-diferenciais2.jpg'),
        require('@/assets/header-lgpd.png'),
        require('@/assets/header-estrutura.png'),
        require('@/assets/header-parceiros.jfif'),
        require('@/assets/banner-inicio.jpg'),
        require('@/assets/header-produtos.jpg'),
        require('@/assets/header-sobre.jpg'),
        require('@/assets/header-normas-setoriais2.jpg'),
        require('@/assets/estoque.jpg'),
        require('@/assets/header-politica-privacidade.jpg'),
        require('@/assets/header-praticas-antissuborno.jpg'),
        require('@/assets/header-responsabilidade-social.png'),
        require('@/assets/header-seguranca-dados.jpg'),
        require('@/assets/clipes-uni-background.png'),
        require('@/assets/header-parceria.jfif'),
        require('@/assets/header-termos-legais2.jpg'),
        require('@/assets/header-trabalhe-conosco.png'),
        require('@/assets/header-trabalhe-conosco-mini.png'),
      ];

      // Pré-carrega cada imagem
      bannerImages.forEach(imageSrc => {
        const img = new Image();
        img.src = imageSrc;
        this.preloadedImages.push(img);
      });
    }
  }
};
</script>

<style>
body,
html {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
}

#app {
  font-family: 'Matter', 'Segoe UI', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #300303;
  position: relative;
}

/* Tela de manutenção */
.maintenance-screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #931414 0%, #470404 50%, #931414 100%);
}

.maintenance-card {
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  max-width: 700px;
  width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  padding: 35px;
  text-align: center;
  position: relative;
}

.maintenance-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #931414;
  border-radius: 24px 24px 0 0;
}

.maintenance-badge {
  display: inline-block;
  background: #931414;
  color: #fff;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 18px;
  text-transform: uppercase;
}

.maintenance-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 12px;
}

.maintenance-icon {
  font-size: 40px;
}

.maintenance-card h1 {
  font-size: 1.8rem;
  color: #470404;
  font-weight: 800;
  margin: 0;
}

.maintenance-subtitle {
  font-size: 1.1rem;
  color: #931414;
  margin: 0 0 18px;
  font-weight: 600;
}

.maintenance-message {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px;
}

.maintenance-message strong {
  color: #470404;
  font-weight: 600;
}

.maintenance-info-box {
  background: #fff5f5;
  border-left: 5px solid #931414;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: left;
}

.maintenance-info-box h3 {
  color: #931414;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 8px;
  font-weight: 800;
}

.maintenance-info-box p {
  color: #555;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.maintenance-contact {
  border-top: 2px solid #f0f0f0;
  padding-top: 18px;
  margin-top: 18px;
}

.maintenance-contact h3 {
  color: #470404;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 15px;
  font-weight: 800;
}

.maintenance-contact-item {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 120px;
  padding: 10px 16px;
  border-radius: 12px;
  background: #f0f0f0;
  color: #931414;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
}

.maintenance-contact-item span:first-child {
  font-size: 24px;
}

.maintenance-logo-section {
  margin-top: 18px;
  padding-top: 15px;
  border-top: 2px solid #f0f0f0;
}

.maintenance-logo {
  max-width: 200px;
}

.maintenance-support-info {
  background: #f8f9fa;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 11px;
  color: #999;
  margin: 12px 0 0;
}

/* Overlay de transição azul */
.page-transition-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #470404, #300303);
  z-index: 9999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-out;
  display: flex;
  justify-content: center;
  align-items: center;
}

.page-transition-overlay.active {
  opacity: 1;
  pointer-events: all;
  transition: opacity 0.5s ease-in;
}

/* Container e animação da logo */
.transition-logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.transition-logo {
  max-width: 1060px;
  height: auto;
  animation: logoAnimation 1.5s ease-in-out;
  filter: drop-shadow(0 8px 30px rgba(0, 0, 0, 0.35));
}

@keyframes logoAnimation {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }

  50% {
    opacity: 1;
    transform: scale(1.1);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Transição padrão - mais suave */
.page-enter-active {
  transition: opacity 1.2s ease, transform 1.2s ease;
}

.page-leave-active {
  transition: opacity 1s ease, transform 1s ease;
}

.page-enter {
  opacity: 0;
  transform: translateY(30px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

/* Transição deslizante para a esquerda - mais suave */
.slide-left-enter-active {
  transition: all 1.2s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.slide-left-leave-active {
  transition: all 1s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.slide-left-enter {
  opacity: 0;
  transform: translateX(80px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-80px);
}

/* Transição de fade - mais suave */
.fade-enter-active {
  transition: opacity 1.2s ease;
}

.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Estilos para links */
a {
  transition: color 0.3s ease, background-color 0.3s ease;
}

/* Estilo para links ativos */
.router-link-active {
  transition: all 0.3s ease;
}

/* Estilo para seleção de texto */
::selection {
  background-color: rgba(71, 4, 4, 0.2);
}

::-moz-selection {
  background-color: rgba(71, 4, 4, 0.2);
}

@media (max-width: 576px) {
  .transition-logo {
    max-width: 325px;
  }
}

@media (max-width: 400px) {
  .transition-logo {
    max-width: 260px;
  }
}
</style>

